import { CognitoApiError } from "./cognito-api";

/** What an Account › Security action was doing when it failed: the same refusal means different things on each. */
export type SecurityAction = "passkey-add" | "passkey-remove" | "authenticator" | "device" | "password" | "sign-out";

/**
 * Account › Security's failures in Starsach's words (R10, ported from gairm/lms 596e644): what happened and what to do,
 * never Cognito's message, an exception's name or the browser's. Starsach's own errors (an API refusal, Confirm it's
 * you closed, a sign-out while it was open) already speak in words and pass through with their request id.
 */
export function securityErrorWords(err: unknown, action: SecurityAction): string {
  const name = (err as { name?: unknown } | null)?.name;
  if (err instanceof CognitoApiError) {
    switch (err.code) {
      case "NotAuthorizedException":
        if (action === "password" && !/token|expired|revoked/i.test(err.message)) return "Your current password isn't right. Check it and try again. If you've forgotten it, sign out and choose Forgot your password?.";
        return "You've been signed out, so nothing was changed. Sign in again, then try again.";
      case "InvalidPasswordException":
        return "That new password can't be used. It may miss one of the rules, or it may have appeared in a data breach. Choose a different one.";
      case "CodeMismatchException":
      case "EnableSoftwareTokenMFAException":
        return "That code didn't work. Codes change every 30 seconds, so enter the one your authenticator app shows now.";
      case "LimitExceededException":
      case "TooManyRequestsException":
      case "TooManyFailedAttemptsException":
        return "There have been too many tries just now. Wait 15 minutes, then try again. Nothing was changed.";
      case "TimeoutError":
        return NETWORK;
      default:
        return UNEXPECTED;
    }
  }
  if (action === "passkey-add" && (name === "NotAllowedError" || name === "AbortError")) return "The passkey prompt was closed, so no passkey was added. Choose Add a passkey to try again.";
  if (action === "passkey-add" && name === "InvalidStateError") return "This device already has a passkey for you. You don't need to add another one here.";
  if (action === "passkey-add" && name === "NotSupportedError") return "This browser or device can't save a passkey. Try again on a device that can.";
  if (err instanceof TypeError) return NETWORK;
  if (typeof DOMException !== "undefined" && err instanceof DOMException) return UNEXPECTED;
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === "string" && message && (name === "ApiRequestError" || name === "StepUpCanceled" || name === "StepUpSessionEnded") ? message : UNEXPECTED;
}

/** The error as ErrorState shows it: Starsach's words, and the request id when the API gave one. */
export function securityError(err: unknown, action: SecurityAction): { message: string; requestId?: string } {
  const requestId = (err as { requestId?: unknown } | null)?.requestId;
  return { message: securityErrorWords(err, action), ...(typeof requestId === "string" ? { requestId } : {}) };
}

const NETWORK = "We couldn't reach the sign-in service. Check your connection, then try again. Nothing was changed.";
const UNEXPECTED = "Something went wrong, and nothing was changed. Try again. If it keeps happening, tell support.";
